"use client";

import { Users, Edit, Trash2, Crown, UserCog, ChevronDown, Unlink, AlertTriangle, UserPlus } from "lucide-react";
import { useState, useEffect } from "react";
import { useRouter } from "nextjs-toploader/app";

import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { Badge } from "@/app/_module/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/app/_module/components/ui/dropdown-menu";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";

import { useGetLeadHouseholds, useRemoveHouseholdMember, useSwitchPrimaryMember, useDisbandHousehold, useRestoreHouseholdMember } from "../hooks/households";
import { HouseholdMember } from "../types/HouseholdMember";
import type { Policy } from "../types/lead-with-policies-type";
import DeleteHouseholdMemberDialog from "./DeleteHouseholdMemberDialog";
import AddLeadToHouseholdDialog from "./AddLeadToHouseholdDialog";

interface LeadHouseholdSectionProps {
  leadId: string;
  leadName?: string;
  simplified?: boolean;
  /** This lead's policies: with no household, "Add lead" is only for a client with a sold one. */
  policies?: Policy[];
}

/**
 * ✅ FIXED: Helper function to safely unwrap API responses
 */
function unwrapApiResponse(response: any): any[] {
  if (!response) return [];
  
  // Try different possible structures
  // 1. Nested data.data (most common)
  if (response?.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  
  // 2. Direct data property
  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }
  
  // 3. Response is an array itself
  if (Array.isArray(response)) {
    return response;
  }
  
  return [];
}

export const LeadHouseholdSection = function({
  leadId,
  leadName,
  simplified = false,
  policies = [],
}: LeadHouseholdSectionProps) {
  const router = useRouter();
  const [deletingMember, setDeletingMember] = useState<HouseholdMember | null>(null);
  const [confirmingDisband, setConfirmingDisband] = useState(false);
  const [restoringMember, setRestoringMember] = useState<any | null>(null);
  const [adding, setAdding] = useState(false);

  const { data: householdsResponse, isLoading, error} = useGetLeadHouseholds(leadId);
  const removeMemberMutation = useRemoveHouseholdMember();
  const switchPrimaryMutation = useSwitchPrimaryMember();
  const disbandMutation = useDisbandHousehold();
  const restoreMutation = useRestoreHouseholdMember();

  // ✅ FIXED: Properly unwrap the API response
  const allMembers = unwrapApiResponse(householdsResponse);

  // Find if current lead is the primary
  const currentLeadIsPrimary = allMembers.some(
    (member: any) => member.id === leadId && member.isPrimary
  );

  // Filter members based on whether we're viewing the primary or not
  // - If viewing PRIMARY: exclude the primary from the table (don't show yourself)
  // - If viewing NON-PRIMARY: show ALL members including the primary
  const householdMembers = currentLeadIsPrimary
    ? allMembers.filter((member: any) => member.id !== leadId)
    : allMembers;

  // Get household ID for switching primary
  const householdId = allMembers.length > 0 ? allMembers[0].householdId : null;
  const primaryName = allMembers.find((member: any) => member.isPrimary)?.name;
  // Owner 10-29: a lead already in a household, or one you sold a policy to, can take other leads
  // in (the backend refuses the rest too).
  const canAddLeads = allMembers.length > 0 || policies.some((policy) => policy.policyStage === "sold");

  // canOpen === false: the member sits outside this agent's leads (often another
  // agency). Opening it would only say "Lead not found", so it gets Restore or a
  // plain "held" label instead. Older API responses have no flag: treat as openable.
  const isLocked = (member: any) => member.canOpen === false;

  const handleConfirmRestore = async () => {
    if (!restoringMember) return;
    const id = restoringMember.id;
    try {
      await restoreMutation.mutateAsync({ leadId: id });
      setRestoringMember(null);
      router.push(`/leads/${id}`);
    } catch {
      // Error surfaced by the service layer's toast
    }
  };

  const handleEdit = (member: HouseholdMember) => {
    router.push(`/leads/${member.id}`);
  };

  const handleDelete = (member: HouseholdMember) => {
    setDeletingMember(member);
  };

  const handleConfirmDelete = async () => {
    if (deletingMember) {
      try {
        await removeMemberMutation.mutateAsync({
          leadId: deletingMember.id,
        });
        setDeletingMember(null);
      } catch (error) {
        // Error handled by mutation
      }
    }
  };

  const handleDisband = async () => {
    if (!householdId) return;
    try {
      await disbandMutation.mutateAsync({ householdId });
      setConfirmingDisband(false);
      // Nothing to navigate to — this lead simply stops being a household member,
      // and the section unmounts once the refetch returns an empty list.
    } catch {
      // Error surfaced by the service layer's toast
    }
  };

  const handleSwitchPrimary = async (member: HouseholdMember) => {
    if (!householdId) {
      return;
    }

    try {
      await switchPrimaryMutation.mutateAsync({
        householdId,
        newPrimaryLeadId: member.id,
      });
      // After switching, navigate to the new primary's page
      router.push(`/leads/${member.id}`);
    } catch (error) {
      // Error handled by mutation
    }
  };

  // Don't show loading state - prevents flash when there's no household data
  // The section will simply appear when data is available
  if (isLoading) {
    return null;
  }

  if (error) {
    // Don't show the section if there's an error loading - fail silently
    return null;
  }

  const addDialog = (
    <AddLeadToHouseholdDialog
      open={adding}
      onOpenChange={setAdding}
      leadId={leadId}
      leadName={leadName}
      householdId={householdId}
      memberIds={allMembers.map((member: any) => member.id)}
    />
  );

  // No household: grouping only joins on a shared phone or address, so agents add the rest here.
  if (allMembers.length === 0) {
    if (!canAddLeads) return null;
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Household
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add lead
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Not in a household yet.</p>
          </CardContent>
        </Card>
        {addDialog}
      </>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          {/* Wraps: in the narrow column the buttons used to squeeze the title onto three lines. */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Household Members ({householdMembers.length})
            </CardTitle>
            {allMembers.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
              {canAddLeads && (
                <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add lead
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmingDisband(true)}
                disabled={disbandMutation.isPending || !householdId}
                className="text-destructive hover:text-destructive"
              >
                <Unlink className="h-4 w-4 mr-2" />
                Disband
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={switchPrimaryMutation.isPending}
                  >
                    <UserCog className="h-4 w-4 mr-2" />
                    Make Primary
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Select Primary Member</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {allMembers.map((member: any) => {
                    const isPrimary = member.isPrimary === true;
                    return (
                      <DropdownMenuItem
                        key={member.id}
                        onClick={() => !isPrimary && !isLocked(member) && handleSwitchPrimary(member)}
                        disabled={isPrimary || isLocked(member) || switchPrimaryMutation.isPending}
                        className={isPrimary ? "bg-primary/5" : "cursor-pointer"}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            {isPrimary && <Crown className="h-4 w-4 text-primary" />}
                            <span className={isPrimary ? "font-medium text-primary" : ""}>
                              {member.name || 'Unknown'}
                            </span>
                          </div>
                          {isPrimary && (
                            <Badge variant="default" className="bg-primary ml-2">
                              Primary
                            </Badge>
                          )}
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {householdMembers.length === 0 ? (
            // When viewing as primary, no other members to show yet
            <div className="flex flex-col items-center justify-center py-4 text-center">
              <p className="text-sm text-muted-foreground">
                You are the primary household member. Add family members when creating additional policies.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Relationship</TableHead>
                  {!simplified && (
                    <>
                      <TableHead>Percentage</TableHead>
                      <TableHead>Age</TableHead>
                      <TableHead>Created</TableHead>
                    </>
                  )}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {householdMembers.map((member: any) => {
                  const isPrimary = member.isPrimary === true;
                  return (
                    <TableRow
                      key={member.id}
                      className={isPrimary ? "bg-primary/5 hover:bg-primary/10" : ""}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {member.name || 'Unknown'}
                          {isPrimary && (
                            <span title="Primary Household Member">
                              <Crown className="h-4 w-4 text-primary" />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isPrimary ? (
                          <Badge variant="default" className="bg-primary">
                            Primary
                          </Badge>
                        ) : member.relationship ? (
                          <Badge variant="outline">
                            {member.relationship}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      {!simplified && (
                        <>
                          <TableCell className="text-muted-foreground">
                            {member.percentage ? `${member.percentage}%` : '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {member.age || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {member.createdAt ? (() => {
                              // Parse date safely - handle both ISO datetime and YYYY-MM-DD formats
                              const dateStr = member.createdAt;
                              let date: Date;
                              if (dateStr.includes('T')) {
                                date = new Date(dateStr);
                              } else {
                                const [year, month, day] = dateStr.split('-').map(Number);
                                date = new Date(year, month - 1, day, 12, 0, 0, 0);
                              }
                              return date.toLocaleDateString();
                            })() : '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-right">
                        {isLocked(member) ? (
                          member.restorable ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRestoringMember(member)}
                              disabled={restoreMutation.isPending}
                              title="Add this household member to your leads"
                            >
                              <UserPlus className="h-4 w-4 mr-2" />
                              Restore to my leads
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {member.heldByAnotherAgent ? "Held by another agent" : "Not in your leads"}
                            </span>
                          )
                        ) : (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(member)}
                            title="View household member details"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {!isPrimary && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(member)}
                              title="Remove from household"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Disband — dissolves the whole group, every member becomes standalone */}
      {confirmingDisband && (
        <AlertDialog open onOpenChange={() => setConfirmingDisband(false)}>
          <ResponsiveAlertDialogContent size="md">
            <AlertDialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <AlertDialogTitle>Disband Household</AlertDialogTitle>
              </div>
              <AlertDialogDescription className="pt-3">
                All <strong>{allMembers.length}</strong> member
                {allMembers.length === 1 ? "" : "s"} will become standalone leads
                with no household.
                <br />
                <br />
                Nothing is deleted, moved or reassigned — only the grouping is
                removed. Leads that genuinely share a phone number or address may
                be regrouped automatically on a future import.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => setConfirmingDisband(false)}
                disabled={disbandMutation.isPending}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDisband}
                disabled={disbandMutation.isPending}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {disbandMutation.isPending ? "Disbanding..." : "Disband Household"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </ResponsiveAlertDialogContent>
        </AlertDialog>
      )}

      {/* Restore: bring a member that sits outside this agent's leads into them */}
      {restoringMember && (
        <AlertDialog open onOpenChange={() => setRestoringMember(null)}>
          <ResponsiveAlertDialogContent size="md">
            <AlertDialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <UserPlus className="h-5 w-5 text-primary" />
                </div>
                <AlertDialogTitle>Restore to your leads</AlertDialogTitle>
              </div>
              <AlertDialogDescription className="pt-3">
                <strong>{restoringMember.name || "This member"}</strong> is part of this household
                but isn&apos;t in your leads yet. Restoring adds them to your leads
                {primaryName ? <> alongside <strong>{primaryName}</strong></> : null}, so the
                whole household stays with you.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => setRestoringMember(null)}
                disabled={restoreMutation.isPending}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmRestore} disabled={restoreMutation.isPending}>
                {restoreMutation.isPending ? "Restoring..." : "Restore to my leads"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </ResponsiveAlertDialogContent>
        </AlertDialog>
      )}

      {canAddLeads && addDialog}

      {/* Delete Dialog */}
      {deletingMember && (
        <DeleteHouseholdMemberDialog
          member={deletingMember}
          onClose={() => setDeletingMember(null)}
          onConfirm={handleConfirmDelete}
          isDeleting={removeMemberMutation.isPending}
        />
      )}
    </>
  );
};

export default LeadHouseholdSection;